import { type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';
import { Lock, Users, Wallet } from 'lucide-react';
import { EmptyState } from '../components/feedback/empty-state.js';
import { PageHeader } from '../components/layout/page-header.js';
import { PayrollSection } from '../components/payroll/payroll-section.js';
import { Badge, Button, Input, Label, ListSkeleton } from '@tailoring-crm/ui';
import { useAuth } from '../lib/auth/auth-context.js';
import { usePayoutOverview } from '../lib/payouts/use-payouts.js';
import { formatCurrency, formatDate } from '../lib/format.js';

/**
 * Who is owed what, and what the shop's wages come to if everybody were paid tonight.
 *
 * The single figure at the top is what an owner opens this screen for, so it is the first thing
 * on it and it is stated in words: total outstanding wage liability. Everything below is the
 * working — one row per person, ordered by what they are owed, because the person at the top is
 * the person to settle first.
 *
 * A karigar never lands here. Their own earnings are a different screen with a different shape,
 * and the API refuses this one outright — so the page says so rather than rendering a page of
 * failed requests.
 */
export function PayoutsPage(): ReactElement {
  const { t } = useTranslation();
  const { role } = useAuth();
  const [params, setParams] = useSearchParams();

  const from = params.get('from') ?? '';
  const to = params.get('to') ?? '';
  const isStaff = role === 'tailor';

  const overview = usePayoutOverview(
    { ...(from ? { from } : {}), ...(to ? { to } : {}) },
    { enabled: !isStaff },
  );

  if (isStaff) {
    return (
      <>
        <PageHeader title={t('payouts.title')} />
        <EmptyState
          icon={Lock}
          title={t('payouts.noAccessTitle')}
          description={t('payouts.noAccessHint')}
          action={
            <Button asChild>
              <Link to="/my-earnings">{t('payouts.goToMyEarnings')}</Link>
            </Button>
          }
        />
      </>
    );
  }

  const setParam = (key: string, value: string): void => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
  };

  const data = overview.data;
  const rows = [...(data?.rows ?? [])].sort(
    (a, b) => b.outstandingPaise - a.outstandingPaise || a.staffName.localeCompare(b.staffName),
  );

  return (
    <>
      <PageHeader title={t('payouts.title')} description={t('payouts.description')} />

      <div className="flex flex-col gap-4">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="payouts-from">{t('payouts.from')}</Label>
            <Input
              id="payouts-from"
              type="date"
              value={from}
              className="max-w-44"
              onChange={(event) => setParam('from', event.target.value)}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="payouts-to">{t('payouts.to')}</Label>
            <Input
              id="payouts-to"
              type="date"
              value={to}
              className="max-w-44"
              onChange={(event) => setParam('to', event.target.value)}
            />
          </div>
        </div>

        {/* The figure this screen exists for. */}
        <div className="rounded-lg border border-border p-4" role="status">
          <p className="text-sm text-muted-foreground">{t('payouts.liability')}</p>
          <p className="text-2xl font-semibold tabular-nums">
            {formatCurrency(data?.totals.outstandingPaise ?? 0)}
          </p>
          <p className="text-sm text-muted-foreground">
            {t('payouts.liabilityHint', {
              earned: formatCurrency(data?.totals.earnedPaise ?? 0),
              paid: formatCurrency(data?.totals.paidPaise ?? 0),
            })}
          </p>
        </div>

        {overview.isPending ? (
          <ListSkeleton rows={4} />
        ) : rows.length === 0 ? (
          <EmptyState
            icon={Users}
            title={t('payouts.emptyTitle')}
            description={t('payouts.emptyHint')}
          />
        ) : (
          <ul className="flex flex-col gap-2">
            {rows.map((row) => (
              <li
                key={row.staffUserId}
                className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border p-3"
              >
                <div className="min-w-0">
                  <p className="flex flex-wrap items-center gap-2 font-medium">
                    {row.staffName}
                    {row.active ? null : <Badge variant="outline">{t('staff.inactive')}</Badge>}
                    {row.advanceOutstandingPaise > 0 ? (
                      <Badge variant="warning">
                        {t('payouts.advanceOutstanding', {
                          amount: formatCurrency(row.advanceOutstandingPaise),
                        })}
                      </Badge>
                    ) : null}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {t('payouts.rowSummary', {
                      earned: formatCurrency(row.earnedPaise),
                      paid: formatCurrency(row.paidPaise),
                    })}
                    {row.lastSettledAt
                      ? ` · ${t('payouts.lastSettled', { date: formatDate(row.lastSettledAt) })}`
                      : ''}
                  </p>
                </div>

                <div className="flex items-center gap-3">
                  <div className="text-right">
                    <p className="font-semibold tabular-nums">
                      {formatCurrency(row.outstandingPaise)}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {t('payouts.unsettledJobs', { count: row.unsettledJobCount })}
                    </p>
                  </div>
                  <Button asChild variant="outline" size="sm">
                    <Link to={`/payouts/staff/${row.staffUserId}`}>
                      <Wallet aria-hidden="true" />
                      {t('payouts.settleAction')}
                    </Link>
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}

        {/* The shop's payroll cycle, below the settlement list and taking nothing from it. */}
        <PayrollSection />
      </div>
    </>
  );
}

export default PayoutsPage;
